import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Typography
} from '@mui/material';
import {
  DesktopWindows as DesktopIcon,
  PhoneAndroid as MobileIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmailTemplateSelector from './EmailTemplateSelector';
import RecipientSelector from './RecipientSelector';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ]
};

export default function EmailComposer({ campaign, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setSubject(campaign.subject);
      setContent(campaign.content);
      setRecipientType(campaign.recipient_type);
    }
  }, [campaign]);

  const handleSave = () => {
    if (!name || !subject || !content) {
      alert('Please fill in all required fields');
      return;
    }

    onSave({
      name,
      subject,
      content,
      recipient_type: recipientType
    });
  };

  const handleTemplateSelect = (template) => {
    setSubject(template.subject);
    setContent(template.content);
    setTemplateDialogOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Campaign Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={() => setTemplateDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Choose Template
          </Button>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Subject Line"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <RecipientSelector
            value={recipientType}
            onChange={setRecipientType}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Email Content
          </Typography>
          <Paper sx={{ mb: 2 }}>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              style={{ minHeight: '300px' }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Preview
          </Typography>
          <ToggleButtonGroup
            value={previewMode}
            exclusive
            onChange={(e, value) => value && setPreviewMode(value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="desktop">
              <DesktopIcon sx={{ mr: 1 }} /> Desktop
            </ToggleButton>
            <ToggleButton value="mobile">
              <MobileIcon sx={{ mr: 1 }} /> Mobile
            </ToggleButton>
          </ToggleButtonGroup>

          <Paper
            sx={{
              p: 2,
              maxWidth: previewMode === 'mobile' ? '375px' : '100%',
              mx: previewMode === 'mobile' ? 'auto' : 0,
              border: '1px solid #ddd'
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Subject: {subject}
            </Typography>
            <Box
              dangerouslySetInnerHTML={{ __html: content }}
              sx={{
                '& img': { maxWidth: '100%' },
                '& a': { color: '#1976d2' }
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save Campaign
            </Button>
          </Box>
        </Grid>
      </Grid>

      <EmailTemplateSelector
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelect={handleTemplateSelect}
      />
    </Box>
  );
}
